import * as Yup from 'yup';
import { SUPPORTED_COUNTRIES, VALID_COUNTRIES } from '../../utilities/constants';
import countries from '../utilities/countries';
import provinces from '../utilities/provinces';
import states from '../utilities/states';
import { onlyDigitsValidation, strLenIsBetweenRange } from '../utilities/validation/ValidationHelperMethods';
import { errors, paymentFieldsLengths } from './ValidationMessages';

const createReservationSchema = (cidNumberIsRequired = true) => {
  let creditCardValues = {
    number: Yup.string().required(),
    holder: Yup.string().required(),
    expirationMonth: Yup.string().required(),
    expirationYear: Yup.string().required(),
    type: Yup.string().required()
  };

  if (cidNumberIsRequired) {
    creditCardValues.cidNumber = Yup.string()
      .required()
      .test('is-number', errors.CVV_ONLY_NUMBERS, val => onlyDigitsValidation(val))
      .test('len', errors.CVV_LENGTH, val =>
        strLenIsBetweenRange(val, paymentFieldsLengths.CVV_MIN - 1, paymentFieldsLengths.CVV_MAX + 1)
      );
  }

  return Yup.object().shape({
    addOns: Yup.array()
      .of(
        Yup.object().shape({
          code: Yup.string().required(),
          date: Yup.string()
            .nullable()
            .notRequired(),
          pricingKey: Yup.string()
            .nullable()
            .notRequired(),
          quantity: Yup.number()
            .positive()
            .integer()
            .notRequired()
        })
      )
      .default([])
      .notRequired(),
    arrival: Yup.string()
      .nullable()
      .notRequired(),
    departure: Yup.string()
      .nullable()
      .notRequired(),
    contactInfo: Yup.object().shape({
      gender: Yup.number()
        .oneOf([1, 2])
        .required(),
      email: Yup.string()
        .email()
        .required(),
      firstName: Yup.string().required(),
      lastName: Yup.string().required(),
      phoneNumber: Yup.string().required(),
      isPhoneUpdated: Yup.boolean().default(false),
      canContactBySms: Yup.boolean().default(false)
    }),
    lodge: Yup.object().shape({
      code: Yup.string().required()
    }),
    payment: Yup.object().shape({
      type: Yup.number()
        .positive()
        .integer()
        .required(),
      billingAddress: Yup.object().shape({
        street1: Yup.string().required(),
        street2: Yup.string()
          .nullable()
          .notRequired(),
        city: Yup.string().required(),
        state: Yup.string()
          .when('country', {
            is: SUPPORTED_COUNTRIES.UnitedStates,
            then: Yup.string()
              .oneOf(states.map(state => state.abbreviation))
              .required()
          })
          .when('country', {
            is: SUPPORTED_COUNTRIES.Canada,
            then: Yup.string()
              .oneOf(provinces.map(province => province.abbreviation))
              .required()
          })
          .when('country', {
            is: val => !VALID_COUNTRIES.includes(val),
            then: Yup.string().notRequired()
          }),
        country: Yup.string()
          .oneOf(countries.map(country => country.code))
          .required(),
        postalCode: Yup.string()
          .when('country', {
            is: val => val === SUPPORTED_COUNTRIES.UnitedStates,
            then: Yup.string().length(5)
          })
          .required()
      }),
      creditCard: Yup.object().when('type', {
        is: 1,
        then: Yup.object().shape(creditCardValues),
        otherwise: Yup.object().notRequired()
      }),
      affirm: Yup.object().when('type', {
        is: 3,
        then: Yup.object().shape({
          token: Yup.string().required()
        }),
        otherwise: Yup.object().notRequired()
      }),
      paypal: Yup.object().when('type', {
        is: 4,
        then: Yup.object().shape({
          nonce: Yup.string().required(errors.PAYPAL_NONCE_MISSING),
          firstName: Yup.string().notRequired(),
          lastName: Yup.string().notRequired(),
          email: Yup.string().notRequired(),
          deviceId: Yup.string()
            .nullable()
            .notRequired()
        }),
        otherwise: Yup.object().notRequired()
      }),
      loyalty: Yup.object().shape({
        offers: Yup.array()
          .of(Yup.string())
          .default([]),
        points: Yup.number()
          .integer()
          .notRequired()
      })
    }),
    product: Yup.object().shape({
      roomCode: Yup.string().required(),
      price: Yup.object().shape({
        pricingKey: Yup.string()
          .nullable()
          .notRequired(),
        promoCode: Yup.string()
          .uppercase()
          .nullable()
          .notRequired(),
        qualifyingId: Yup.string()
          .nullable()
          .notRequired(),
        rateCode: Yup.string()
          .uppercase()
          .nullable()
          .notRequired()
      })
    }),
    totalGuests: Yup.object().shape({
      adults: Yup.string()
        .nullable()
        .notRequired(),
      children: Yup.array()
        .of(Yup.number().integer())
        .default([])
    })
  });
};

export default createReservationSchema;
